import { flyingTulipAssetsFor, flyingTulipConfigFor } from '@1delta/data-sdk'
import { FlyingTulipLendingLensAbi } from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import type { FlyingTulipAssetRaw, FlyingTulipMarketsRaw } from './types'
import { FLYING_TULIP_LENDER_KEY } from './convertPublic'

const EMPTY: FlyingTulipMarketsRaw = {
  hfSafeBps: 0,
  hfTargetBps: 0,
  minEquityUSDWad: '0',
  positionsManager: '',
  oracleRouter: '',
  assets: [],
}

const big = (v: any): bigint | null => {
  try {
    return v == null ? null : BigInt(v)
  } catch {
    return null
  }
}

/**
 * Fetch all Flying Tulip market data for a chain — FULLY ON-CHAIN (there is
 * no API; `api.flyingtulip.com` is analytics). The roster comes from
 * lender-metadata's `AssetSet`-log discovery; every VALUE is read live from
 * the `LendingLens` (config is one ungated Safe call from moving, so the
 * roster snapshot is only the fallback for a failed item).
 *
 * Two multicall rounds, both `allowFailure` per item:
 *  1. per asset: `assetState` + `assetCfg` + caps + pauses +
 *     `priceAndDecimals` — the LAST one deliberately per-asset, never the
 *     batched `pricesUSD`, which reverts whole-batch on any unpriceable
 *     asset (FT itself, on both chains);
 *  2. per asset: `irmSampleAPR(liveIrm, [liveUtil])` — the exact live borrow
 *     APR from the deployed curve, no ported model to drift.
 */
export async function fetchFlyingTulipMarkets(
  chainId: string,
): Promise<FlyingTulipMarketsRaw> {
  const lender = FLYING_TULIP_LENDER_KEY
  const cfg = flyingTulipConfigFor(lender, chainId)
  const roster = flyingTulipAssetsFor(lender, chainId)
  const lens = cfg?.lendingLens
  const assets = roster?.assets ?? []
  if (!lens || assets.length === 0) return EMPTY

  const CALLS_PER_ASSET = 8
  const calls = assets.flatMap((a) => [
    { address: lens, name: 'assetState', params: [a.address] },
    { address: lens, name: 'assetCfg', params: [a.address] },
    { address: lens, name: 'supplyCap', params: [a.address] },
    { address: lens, name: 'borrowCap', params: [a.address] },
    { address: lens, name: 'depositPaused', params: [a.address] },
    { address: lens, name: 'withdrawPaused', params: [a.address] },
    { address: lens, name: 'borrowPaused', params: [a.address] },
    { address: lens, name: 'priceAndDecimals', params: [a.address] },
  ])

  let results: any[]
  try {
    results = (await multicallRetryUniversal({
      chain: chainId,
      calls,
      abi: FlyingTulipLendingLensAbi,
      allowFailure: true,
    })) as any[]
  } catch {
    return EMPTY
  }

  const rows: FlyingTulipAssetRaw[] = assets.map((a, i) => {
    const o = i * CALLS_PER_ASSET
    const state = results[o] as any[] | null
    const acfg = results[o + 1] as any[] | null
    const px = results[o + 7] as any[] | null
    return {
      address: a.address.toLowerCase(),
      symbol: a.symbol,
      name: a.name,
      decimals: a.decimals,
      irm: (acfg?.[0] ? String(acfg[0]) : a.irm).toLowerCase(),
      mmBps: acfg ? Number(acfg[1]) : a.mmBps,
      enabled: acfg ? Boolean(acfg[2]) : a.enabled,
      borrowable: acfg ? Boolean(acfg[3]) : a.borrowable,
      isCollateral: acfg ? Boolean(acfg[4]) : a.collateral,
      supplyCap: big(results[o + 2]) ?? BigInt(a.supplyCap),
      borrowCap: big(results[o + 3]) ?? BigInt(a.borrowCap),
      depositPaused: results[o + 4] == null ? a.depositPaused : Boolean(results[o + 4]),
      withdrawPaused: results[o + 5] == null ? a.withdrawPaused : Boolean(results[o + 5]),
      borrowPaused: results[o + 6] == null ? a.borrowPaused : Boolean(results[o + 6]),
      priceable: a.priceable,
      priceWad: big(px?.[0]),
      cash: big(state?.[0]) ?? 0n,
      borrows: big(state?.[1]) ?? 0n,
      reserves: big(state?.[2]) ?? 0n,
      utilWad: big(state?.[3]) ?? 0n,
      borrowAprWad: null,
    }
  })

  // Round 2: live borrow APR at live utilization, per asset (the IRM is live
  // from round 1, so a redeployed curve is picked up in the same cycle).
  try {
    const aprResults = (await multicallRetryUniversal({
      chain: chainId,
      calls: rows.map((r) => ({
        address: lens,
        name: 'irmSampleAPR',
        params: [r.irm, [r.utilWad]],
      })),
      abi: FlyingTulipLendingLensAbi,
      allowFailure: true,
    })) as any[]
    rows.forEach((r, i) => {
      const arr = aprResults[i]
      r.borrowAprWad = Array.isArray(arr) ? big(arr[0]) : null
    })
  } catch {
    // Rates stay null; the converter emits 0% rather than dropping the market.
  }

  return {
    hfSafeBps: roster?.marginHfSafeBps ?? 15000,
    hfTargetBps: roster?.marginHfTargetBps ?? 12500,
    minEquityUSDWad: roster?.marginMinEquityUSDWad ?? '0',
    positionsManager: (cfg?.positionsManager ?? '').toLowerCase(),
    oracleRouter: (cfg?.oracleRouter ?? '').toLowerCase(),
    assets: rows,
  }
}
